import { useState } from "react";

/* PACKAGES */
import { motion } from "framer-motion";
import { ToastContainer } from "react-toastify";
import { PopupModal } from "react-calendly";

/* MATERIAL TAILWIND COMPONENTS */
import { Button } from "@material-tailwind/react";

/* ANIMATION */
import { animation, containerAnimation, GCP_BUCKET_URL } from "config";

/* COMPONENTS */
import {
  CallToAction,
  CounterComponent,
  FrequentlyAskedQuestions,
} from "components/homepage";

/* ICONS */
import { FaCircleCheck } from "react-icons/fa6";

const OKRManagement = () => {
  const [calendlyModal, setCalendlyModal] = useState(false);

  return (
    <motion.section
      variants={containerAnimation}
      initial="hidden"
      animate="visible"
      className="bg-transparent"
    >
      {/* TOAST CONTAINER */}
      <ToastContainer theme="colored" closeOnClick autoClose={5000} />

      {/* BANNER */}
      <motion.div
        variants={animation}
        className="p-0 py-5 xl:p-5 lg:p-5 relative h-auto xl:h-featureBanner lg:h-featureBanner"
      >
        {/* LEFT SECTION */}
        <div className="container mx-auto h-full">
          <div className="h-full flex items-center">
            <div className="w-full xl:w-2/5 lg:w-2/5">
              <div className="bg-brandColor bg-opacity-10 rounded-full border border-brandColor w-fit px-4 py-1">
                <p className="text-sm font-asap font-bold text-brandColor">
                  Organizational Management
                </p>
              </div>
              <div className="mt-5">
                <h1 className="text-3xl xl:text-4xl lg:text-4xl font-asap font-bold text-black leading-snug">
                  Execute Strategy, Don't Just Plan It
                </h1>
                <h2 className="mt-5 text-lg xl:text-xl lg:text-xl font-asap font-normal text-greyText tracking-tight">
                  Bridge the gap between C-Suite strategy and daily execution.
                  Aligned Rewards is the scalable Operating System that aligns
                  huge corporations and agile startups alike, accelerating your
                  path to market dominance.
                </h2>
                <div className="mt-5 xl:mt-8 lg:mt-8">
                  <div className="flex items-center gap-3">
                    <Button
                      className="shadow-none hover:shadow-none rounded-lg bg-brandColor normal-case text-base font-asap font-bold text-white hover:bg-opacity-80 transition-all duration-300 ease-in-out"
                      onClick={() => setCalendlyModal(!calendlyModal)}
                    >
                      Schedule a Demo
                    </Button>
                  </div>
                </div>
                <div className="mt-8">
                  <div className="flex items-center gap-2">
                    <FaCircleCheck className="w-5 h-5 text-green-500" />
                    <p className="text-base font-asap font-normal text-greyText">
                      30 day free trial
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <FaCircleCheck className="w-5 h-5 text-green-500" />
                    <p className="text-base font-asap font-normal text-greyText">
                      No credit card required
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT SECTION */}
        <div className="relative xl:absolute lg:absolute top-5 right-0 w-full xl:w-1/2 lg:w-1/2 h-full overflow-hidden pl-5 xl:pl-0 lg:pl-0">
          <div className="h-full bg-gradient-to-tr from-blue-100 to-white rounded-l-2xl ">
            <div className="w-[150vh] h-[40vh] xl:h-featureBanner lg:h-featureBanner relative xl:absolute lg:absolute top-0 xl:top-5 lg:top-5 left-0 xl:left-5 lg:left-5">
              <img
                src={`${GCP_BUCKET_URL}/rename-okr-development.png`}
                className="w-full h-[80vh]"
                alt="OKR Management with Aligned Rewards"
              />
            </div>
          </div>
        </div>
      </motion.div>

      {/* COUNTERS */}
      <motion.div variants={animation} className="mt-5 xl:mt-10 lg:mt-10">
        <div className="bg-footer bg-opacity-5 py-10">
          <div className="container mx-auto">
            <div className="grid grid-cols-1 xl:grid-cols-3 lg:grid-cols-3 gap-10 xl:gap-20 lg:gap-20">
              <div className="col-span-1">
                <CounterComponent
                  title="Connect Vision to Reality"
                  description="Stop strategy from dying in a PDF. Our Dynamic OKR Tools translate high-level company vision into actionable milestones, ensuring every team knows exactly what 'Success' looks like."
                />
              </div>
              <div className="col-span-1">
                <CounterComponent
                  title="Automate Progress Reporting"
                  description="Reduce the 'Reporting Tax' by 30%. Our system automatically rolls up key results into executive dashboards, allowing leadership to focus on Strategic Initiatives rather than chasing status updates."
                />
              </div>
              <div className="col-span-1">
                <CounterComponent
                  title="Eliminate Operational Silos"
                  description="Simplify the complexity of scale. We force cross-functional collaboration by linking departmental goals, ensuring that Marketing, Product, and Sales move in Intensified Unison."
                />
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* STATISTICS - LEFT IMAGE */}
      <motion.div variants={animation} className="py-5 xl:py-14 lg:py-14">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 xl:grid-cols-2 lg:grid-cols-2 gap-y-5 xl:gap-10 lg:gap-10 items-center">
            <div className="col-span-1">
              <div className="w-full h-[50vh] overflow-hidden shadow-md rounded-xl">
                <div className="w-full h-full rounded-xl bg-gradient-to-br from-blue-100 to-white relative">
                  <div className="w-[85vh] h-auto relative xl:absolute lg:absolute top-5 xl:top-14 lg:top-14 -left-5 xl:left-5 lg:left-5">
                    <img
                      src={`${GCP_BUCKET_URL}/okr-management/01.png`}
                      className="w-full h-[55vh] rounded-lg"
                      alt=" Get the overall statistics of your objectives and key results at one place"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-span-1 flex items-center justify-center">
              <div className="w-full xl:w-11/12 lg:w-11/12">
                <h2 className="text-2xl xl:text-3xl lg:text-3xl font-asap font-bold text-black leading-snug">
                  A Single Source of Truth for Organizational Health
                </h2>
                <h5 className="text-base xl:text-lg lg:text-lg font-asap font-normal text-greyText mt-3">
                  Ditch the fragmented spreadsheets. Access comprehensive,
                  real-time statistics for your Objectives and Key Results
                  (OKRs) centralized in one location. Know exactly where the
                  business stands at a glance.
                </h5>
                <div className="mt-5">
                  <div className="flex items-start gap-3">
                    <FaCircleCheck className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <p className="text-base xl:text-lg lg:text-lg font-asap font-normal text-greyText">
                      <span className="font-semibold text-black">
                        Aggregated Performance Scores:
                      </span>{" "}
                      Get a combined "Health Score" of all your important Key
                      Results. Instantly identify which departments are winning
                      and which need support.
                    </p>
                  </div>
                  <div className="flex items-start gap-3 mt-5">
                    <FaCircleCheck className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <p className="text-base xl:text-lg lg:text-lg font-asap font-normal text-greyText">
                      <span className="font-semibold text-black">
                        Velocity Tracking:
                      </span>{" "}
                      Gain insights into "Pace vs. Plan." See how much of the
                      fiscal period has passed versus how much of the objective
                      is completed to predict success probability.
                    </p>
                  </div>
                </div>
                <div className="mt-5 xl:mt-10 lg:mt-10">
                  <Button
                    className="shadow-none hover:shadow-none rounded-lg bg-brandColor normal-case text-base font-asap font-bold text-white hover:bg-opacity-80 transition-all duration-300 ease-in-out"
                    onClick={() => setCalendlyModal(!calendlyModal)}
                  >
                    Schedule a Demo
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* ALIGNMENT - RIGHT IMAGE */}
      <motion.div
        variants={animation}
        className="py-5 xl:py-14 lg:py-14 bg-footer bg-opacity-5"
      >
        <div className="container mx-auto">
          <div className="grid grid-cols-1 xl:grid-cols-2 lg:grid-cols-2 gap-y-5 xl:gap-10 lg:gap-10 items-center">
            <div className="col-span-1 order-2 xl:order-1 lg:order-1">
              <div className="w-full xl:w-11/12 lg:w-11/12">
                <h2 className="text-2xl xl:text-3xl lg:text-3xl font-asap font-bold text-black leading-snug">
                  Cascading Alignment: From CEO to Intern
                </h2>
                <h5 className="text-base xl:text-lg lg:text-lg font-asap font-normal text-greyText mt-3">
                  The #1 killer of strategy is misalignment. Maximize results by
                  synchronizing team efforts with top-level objectives,
                  fostering a culture where every employee understands their
                  impact on the bottom line.
                </h5>
                <div className="mt-5">
                  <div className="flex items-start gap-3">
                    <FaCircleCheck className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <p className="text-base xl:text-lg lg:text-lg font-asap font-normal text-greyText">
                      <span className="font-semibold text-black">
                        Dynamic Sync:
                      </span>{" "}
                      Enable your organization to "Cascade" objectives downward.
                      When the company goal shifts, team goals adjust
                      automatically.
                    </p>
                  </div>
                  <div className="flex items-start gap-3 mt-3">
                    <FaCircleCheck className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <p className="text-base xl:text-lg lg:text-lg font-asap font-normal text-greyText">
                      <span className="font-semibold text-black">
                        Contribution Clarity:
                      </span>{" "}
                      Achieve optimal results by making the "Why" visible.
                      Employees engage deeper when they see how their individual
                      contribution drives the company's "North Star."
                    </p>
                  </div>
                </div>
                <div className="mt-5 xl:mt-10 lg:mt-10">
                  <Button
                    className="shadow-none hover:shadow-none rounded-lg bg-brandColor normal-case text-base font-asap font-bold text-white hover:bg-opacity-80 transition-all duration-300 ease-in-out"
                    onClick={() => setCalendlyModal(!calendlyModal)}
                  >
                    Schedule a Demo
                  </Button>
                </div>
              </div>
            </div>
            <div className="col-span-1 order-1 xl:order-2 lg:order-2">
              <div className="w-full h-[50vh] overflow-hidden shadow-md rounded-xl">
                <div className="w-full h-full rounded-xl bg-gradient-to-br from-blue-100 to-white relative">
                  <div className="w-[85vh] h-auto relative xl:absolute lg:absolute top-5 xl:top-14 lg:top-14 -left-5 xl:left-5 lg:left-5">
                    <img
                      src={`${GCP_BUCKET_URL}/okr-management/02.png`}
                      className="w-full h-[55vh] rounded-lg"
                      alt="Harmonize your teams with objectives for enhanced outcomes"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* KEY RESULTS - LEFT IMAGE */}
      <motion.div variants={animation} className="py-5 xl:py-14 lg:py-14">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 xl:grid-cols-2 lg:grid-cols-2 gap-y-5 xl:gap-10 lg:gap-10 items-center">
            <div className="col-span-1">
              <div className="w-full h-[50vh] overflow-hidden shadow-md rounded-xl">
                <div className="w-full h-full rounded-xl bg-gradient-to-br from-blue-100 to-white relative">
                  <div className="w-[85vh] h-auto relative xl:absolute lg:absolute top-5 xl:top-14 lg:top-14 -left-5 xl:left-5 lg:left-5">
                    <img
                      src={`${GCP_BUCKET_URL}/okr-management/03.png`}
                      className="w-full h-[55vh] rounded-lg"
                      alt="Harmonize your teams with objectives for enhanced outcomes"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-span-1 flex items-center justify-center">
              <div className="w-full xl:w-11/12 lg:w-11/12">
                <h2 className="text-2xl xl:text-3xl lg:text-3xl font-asap font-bold text-black leading-snug">
                  Quantify the "Unquantifiable"
                </h2>
                <h5 className="text-base xl:text-lg lg:text-lg font-asap font-normal text-greyText mt-3">
                  Not all goals are created equal. Stay on target with flexible
                  measurement tools that track hard numbers (Revenue) alongside
                  soft metrics (Culture/Engagement), ensuring a holistic view of
                  performance.
                </h5>
                <div className="mt-5">
                  <div className="flex items-start gap-3">
                    <FaCircleCheck className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <p className="text-base xl:text-lg lg:text-lg font-asap font-normal text-greyText">
                      <span className="font-semibold text-black">
                        Flexible KPI Engine:
                      </span>{" "}
                      Utilize "Measurable Key Results" for quantifiable outcomes
                      (e.g., $1M Revenue) and "Trackable Key Results" for
                      milestone-based projects that can't be measured by math
                      alone.
                    </p>
                  </div>
                  <div className="flex items-start gap-3 mt-5">
                    <FaCircleCheck className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <p className="text-base xl:text-lg lg:text-lg font-asap font-normal text-greyText">
                      <span className="font-semibold text-black">
                        Frictionless Tracking:
                      </span>{" "}
                      With our user-friendly interface, updating progress takes
                      seconds, ensuring your data is always fresh and your
                      decisions are always accurate.
                    </p>
                  </div>
                </div>
                <div className="mt-5 xl:mt-10 lg:mt-10">
                  <Button
                    className="shadow-none hover:shadow-none rounded-lg bg-brandColor normal-case text-base font-asap font-bold text-white hover:bg-opacity-80 transition-all duration-300 ease-in-out"
                    onClick={() => setCalendlyModal(!calendlyModal)}
                  >
                    Schedule a Demo
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* FREQUENTLY ASKED QUESTIONS */}
      <FrequentlyAskedQuestions />

      {/* CALL TO ACTION */}
      <CallToAction />

      {/* CALENDLY MODAL */}
      <PopupModal
        open={calendlyModal}
        onModalClose={() => setCalendlyModal(!calendlyModal)}
        rootElement={document.getElementById("root")}
        url="https://calendly.com/contact-ucp8/30min"
      />
    </motion.section>
  );
};

export default OKRManagement;
